import type { NotifiedRecipientItem } from './replyPreviewRecipients';

export type QuotedAuthorIdentity = {
    displayName: string;
    email?: string | null;
    userId?: string | null;
};

type ReplyRecipientsLike = {
    to?: {
        emails?: string[];
        signalUsers?: string[];
    } | null;
    cc?: {
        emails?: string[];
        signalUsers?: string[];
    } | null;
};

function normalizeEmail(value?: string | null): string {
    return String(value || '').trim().toLowerCase();
}

/** Prefer a first name for the warning copy; fall back to the quote label. */
export function quotedAuthorDisplayName(
    author: { firstname?: string; email?: string } | null | undefined,
    authorLabel: string,
): string {
    const first = String(author?.firstname || '').trim();
    if (first && first !== '—')
        return first;
    const label = String(authorLabel || '').trim();
    if (label)
        return label;
    return String(author?.email || '').trim();
}

export function canAddQuotedAuthorToRecipients(author: QuotedAuthorIdentity | null | undefined): boolean {
    if (!author)
        return false;
    return !!(normalizeEmail(author.email) || String(author.userId || '').trim());
}

/** Whether the quoted author is already covered by stored reply recipients (or client fallback). */
export function isQuotedAuthorAmongRecipients(
    author: QuotedAuthorIdentity | null | undefined,
    replyRecipients: ReplyRecipientsLike | null | undefined,
    clientFallbackEmail?: string | null,
): boolean {
    if (!author)
        return true;

    const email = normalizeEmail(author.email);
    const userId = String(author.userId || '').trim();
    const emails = [
        ...(replyRecipients?.to?.emails || []),
        ...(replyRecipients?.cc?.emails || []),
        ...(clientFallbackEmail ? [clientFallbackEmail] : []),
    ].map(normalizeEmail).filter(Boolean);

    if (email && emails.includes(email))
        return true;

    if (userId) {
        const signalUsers = [
            ...(replyRecipients?.to?.signalUsers || []),
            ...(replyRecipients?.cc?.signalUsers || []),
        ].map((id) => String(id));
        if (signalUsers.includes(userId))
            return true;
    }

    return false;
}

/** Whether the quoted author appears in the live “who will be notified” preview. */
export function isQuotedAuthorAmongNotified(
    author: QuotedAuthorIdentity | null | undefined,
    notifiedItems: NotifiedRecipientItem[] | null | undefined,
): boolean {
    if (!author)
        return true;

    const email = normalizeEmail(author.email);
    const userId = String(author.userId || '').trim();
    const items = notifiedItems || [];

    return items.some((item) => {
        const key = String(item.key || '').toLowerCase();
        if (email && (key === `email:${email}` || key.endsWith(`:${email}`)))
            return true;
        if (userId && key === `signal-user:${userId.toLowerCase()}`)
            return true;
        return false;
    });
}

/**
 * Show the warning once the preview is ready and the quoted author would not
 * receive the outbound reply. Falls back to stored recipients while preview loads.
 */
export function shouldWarnQuotedAuthorNotRecipient(params: {
    author: QuotedAuthorIdentity | null | undefined;
    currentUserId?: string | null;
    replyRecipients: ReplyRecipientsLike | null | undefined;
    clientFallbackEmail?: string | null;
    notifiedItems: NotifiedRecipientItem[] | null | undefined;
    previewReady: boolean;
}): boolean {
    const { author, currentUserId, replyRecipients, clientFallbackEmail, notifiedItems, previewReady } = params;
    if (!canAddQuotedAuthorToRecipients(author))
        return false;
    if (author?.userId && currentUserId && String(author.userId) === String(currentUserId))
        return false;

    if (previewReady)
        return !isQuotedAuthorAmongNotified(author, notifiedItems);

    return !isQuotedAuthorAmongRecipients(author, replyRecipients, clientFallbackEmail);
}
